import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import BarList from '../../components/admin/BarList';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Skeleton,
  StatTile
} from '../../components/admin/ui';
import {
  IconAlert,
  IconCases,
  IconCheck,
  IconClock,
  IconMerchants,
  IconRefresh
} from '../../components/admin/icons';
import { issueTypeLabels, formatNumber } from '../../components/admin/labels';

const StatSkeleton = () => (
  <div className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
    <Skeleton className="h-4 w-24" />
    <Skeleton className="mt-4 h-8 w-16" />
    <Skeleton className="mt-2 h-3 w-28" />
  </div>
);

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchMetrics = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const response = await api.get('/api/admin/dashboard/metrics');
      setMetrics(response.data);
      setUpdatedAt(new Date());
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load dashboard metrics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const cases = metrics?.cases;
  const merchants = metrics?.merchants;

  const issueTypeItems = (metrics?.breakdown?.byIssueType || []).map((item) => ({
    label: issueTypeLabels[item._id] || item._id || 'Unspecified',
    value: item.count
  }));

  const courierItems = (metrics?.breakdown?.byCourier || []).map((item) => ({
    label: item._id || 'Unknown courier',
    value: item.count
  }));

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="font-display text-sm font-700 uppercase tracking-[0.08em] text-ink-500">
            At a glance
          </h2>
          <div className="flex items-center gap-3">
            {updatedAt && (
              <span className="hidden text-xs text-ink-500 sm:inline">
                Updated{' '}
                {updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchMetrics({ silent: true })}
              loading={refreshing}
            >
              {!refreshing && <IconRefresh size={15} />}
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((key) => (
              <StatSkeleton key={key} />
            ))}
          </div>
        ) : error ? (
          <Card>
            <EmptyState
              icon={IconAlert}
              title="Dashboard unavailable"
              description={error}
              action={
                <Button variant="secondary" size="sm" onClick={() => fetchMetrics()}>
                  <IconRefresh size={15} />
                  Try again
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label="Total cases"
              value={formatNumber(cases?.total ?? 0)}
              hint="All cases ever submitted"
              icon={IconCases}
              tone="neutral"
            />
            <StatTile
              label="Open cases"
              value={formatNumber(cases?.open ?? 0)}
              hint="Submitted, evidence prepared or in review"
              icon={IconClock}
              tone="warning"
            />
            <StatTile
              label="Closed cases"
              value={formatNumber(cases?.closed ?? 0)}
              hint={`${formatNumber(cases?.merchantResponded ?? 0)} merchant responses received`}
              icon={IconCheck}
              tone="success"
            />
            <StatTile
              label="Active merchants"
              value={formatNumber(merchants?.total ?? 0)}
              hint={
                merchants?.suspended
                  ? `${formatNumber(merchants.suspended)} currently suspended`
                  : 'None suspended'
              }
              icon={IconMerchants}
              tone={merchants?.suspended ? 'danger' : 'neutral'}
            />
          </div>
        )}
      </section>

      {/* System error notice */}
      {!loading && cases?.systemError > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
          <IconAlert size={18} className="mt-0.5 shrink-0 text-warning-600" />
          <p className="text-sm text-warning-700">
            <span className="font-600">
              {formatNumber(cases.systemError)} case{cases.systemError === 1 ? '' : 's'} marked as
              system error
            </span>{' '}
            — excluded from the breakdowns below and from analytics.
          </p>
        </div>
      )}

      {/* Breakdowns */}
      {!loading && !error && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="Cases by issue type"
              description="What customers are reporting, excluding system errors."
            />
            <CardBody>
              {issueTypeItems.length ? (
                <BarList items={issueTypeItems} />
              ) : (
                <EmptyState
                  icon={IconCases}
                  title="No cases yet"
                  description="Issue types appear here once customers start submitting delivery issues."
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Cases by courier"
              description="Where delivery issues are concentrated."
            />
            <CardBody>
              {courierItems.length ? (
                <BarList items={courierItems} />
              ) : (
                <EmptyState
                  icon={IconAlert}
                  title="No courier data yet"
                  description="Once cases are submitted, courier volumes are broken down here."
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
